// ./src/components/features/CreateEventWizard/steps/BasicsStep.tsx
//
// Step 2 — "The essentials". Title + subtitle (always), a schedule-mode choice
// (single / recurring / agenda — recurring is hidden for one-time occasions like
// a birthday), the umbrella date/time, and the timezone. The time inputs follow
// the browser's 12h/24h locale, end auto-fills to +1h, moving the start keeps the
// duration, and a live duration badge (with an overnight flag) shows beneath.
//
// Uses the shared field family + event controls so it stays in lockstep with the
// event details editor.
//
// #139 — on the QUICK headcount path the step asks only the name and when: no subtitle and no schedule choice (a quick
// event is one date). A note at the top says what the path is, with the way back to the full setup. #148 — the same on
// the quick ticket sale, whose note says what comes next there (where, then the ticket).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, SimpleGrid, Text, Alert, Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { STATUSES } from '@sdflc/utils';

import { useAppEnvironment } from '@/contexts';
import { OrganizerProfile, OrganizerProfileService, listAllPages } from '@/utils/api';
import { AppIcon, TextField, TextAreaField, DateTimeField, TimezoneSelect, SwitchField, SelectField } from '@/components/generic';
import { EventDurationBadge, ScheduleModeField, eventZoneNote } from '@/components/features/Events';
import { addMinutesToDateTime, getFieldError, shiftEndKeepingDuration, isEndBeforeStart, normalizePickerValue } from '@/utils';

import { useEventWizard } from '../WizardContext';
import { QUICK_KIND, SCHEDULE_MODE, EventWizardDraft, quickKindOf } from '../wizardTypes';
import { leaveQuick, wizardOrganizerDefault } from '../wizardSetup';
import { useOptionalEventSetupCatalogContext } from '@/contexts/EventSetupCatalogContext';

export const BasicsStep: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const { isMobile } = useAppEnvironment();
  // ⓘ `saveError` — the server's refusal of a save (the essentials, or a quick Finish) is shown under the input it names.
  const { draft, patchDraft, saveError } = useEventWizard();

  const catalog = useOptionalEventSetupCatalogContext();
  const type = catalog?.findType(draft.eventCategoryId) ?? null;
  const isSpan = draft.scheduleMode !== SCHEDULE_MODE.SINGLE;

  // Remember an explicit end-time clear so we don't keep re-adding the +1h default.
  const endClearedRef = useRef(false);

  // Hide "Repeats" for one-time occasions (a birthday isn't a series).
  // ⚠ Unknown (catalog still loading) counts as ALLOWED — the effect below would otherwise reset a restored
  // "Repeats" choice to single on every cold load.
  const allowRecurring = catalog?.allowsRecurring(draft.eventCategoryId) ?? true;

  // One stable handler for the plain fields (title / subtitle / schedule / timezone).
  const handleChange = useCallback(
    (name: string | undefined, value: unknown) => {
      if (name) patchDraft({ [name]: value } as Partial<EventWizardDraft>);
    },
    [patchDraft],
  );

  // If the type flips to one that can't recur while "Repeats" was selected, fall
  // back to a single event.
  useEffect(() => {
    if (!allowRecurring && draft.scheduleMode === SCHEDULE_MODE.RECURRING) {
      patchDraft({ scheduleMode: SCHEDULE_MODE.SINGLE });
    }
  }, [allowRecurring, draft.scheduleMode, patchDraft]);

  const handleStartChange = useCallback(
    (_name: string | undefined, value: string | null) => {
      if (!value) {
        patchDraft({ startsAt: null });
        return;
      }
      const { startsAt: oldStart, endsAt: end, isAllDay } = draft;
      if (end && oldStart) {
        // Moving the start keeps the duration — shift the end by the same delta.
        patchDraft({ startsAt: value, endsAt: shiftEndKeepingDuration(oldStart, end, value, isAllDay) });
      } else if (!end && !endClearedRef.current && !isAllDay) {
        // No end yet (and not explicitly cleared): default to +1 hour.
        patchDraft({ startsAt: value, endsAt: addMinutesToDateTime(value, 60) });
      } else {
        patchDraft({ startsAt: value });
      }
    },
    [draft, patchDraft],
  );

  const handleEndChange = useCallback(
    (_name: string | undefined, value: string | null) => {
      endClearedRef.current = value == null;
      // Never allow the end to land before the start — snap it to the start.
      const next = value && isEndBeforeStart(draft.startsAt, value) ? draft.startsAt : value;
      patchDraft({ endsAt: next });
    },
    [draft.startsAt, patchDraft],
  );

  const handleAllDayChange = useCallback(
    (_name: string | undefined, checked: boolean) => {
      patchDraft({
        isAllDay: checked,
        startsAt: normalizePickerValue(draft.startsAt, checked),
        endsAt: normalizePickerValue(draft.endsAt, checked),
      });
    },
    [draft.startsAt, draft.endsAt, patchDraft],
  );

  // A2b #54b (f) + #62 P6(d2) — which organizer page lists this event: SHOWN whenever the account has one (owner,
  // 2026-09-30: "automatically select organizer's profile if any, and let user remove it or change to another one"),
  // preselected by the server's own rule (`wizardOrganizerDefault` — the only live page; with several, the host picks).
  // ⚠⚠ Its OWN unfiltered read, never the app-wide `OrganizerProfilesProvider`: that one may still hold the narrowing
  //   of a search on the Organizer pages screen (review, 2026-09-30) — one match of three would be preselected as "the
  //   only page", listing the event on a public page the host never chose. ⚠ A FAILED read shows no picker and sends
  //   nothing, so the server's own rule applies on create, exactly as before this step asked.
  const [organizerProfiles, setOrganizerProfiles] = useState<OrganizerProfile[] | null>(null);

  useEffect(() => {
    let alive = true;

    // Read whole (listAllPages) — the picker offers every live page; a cap here silently dropped the rest (ROADMAP #118).
    void listAllPages(OrganizerProfileService.list, {}).then((res) => {
      if (!alive) {
        return;
      }

      setOrganizerProfiles(
        res.didSucceed() && Array.isArray(res.data) ? (res.data as OrganizerProfile[]).filter((p) => p.status === STATUSES.ACTIVE) : null,
      );
    });

    return () => {
      alive = false;
    };
  }, []);

  const organizerOptions = useMemo(
    () => (organizerProfiles ?? []).filter((p) => p.id).map((p) => ({ value: p.id as string, label: p.name || p.slug || '—' })),
    [organizerProfiles],
  );
  const askOrganizer = organizerOptions.length >= 1;
  const organizerDefault = useMemo(() => wizardOrganizerDefault(organizerProfiles ?? []), [organizerProfiles]);

  // ⚠ Once the picker is ON SCREEN, what it shows is what is sent: the preselected page, or — with several and none
  // picked — "Not on an organizer page", so the draft says null, never undefined (which would let the server attach a
  // page the host was told this event is not on). ⚠ Only once the read has come back (a list not read yet is not "no
  // pages"), and latched: it only ever fills an undefined, never overwrites the host's choice.
  useEffect(() => {
    if (organizerProfiles && askOrganizer && draft.organizerProfileId === undefined) {
      patchDraft({ organizerProfileId: organizerDefault });
    }
  }, [organizerProfiles, askOrganizer, organizerDefault, draft.organizerProfileId, patchDraft]);

  const quick = !!draft.quick;

  return (
    <Stack gap="lg">
      {quick ? (
        <Alert
          variant="light"
          color={quickKindOf(draft) === QUICK_KIND.TICKETS ? 'grape' : 'teal'}
          icon={<AppIcon iconName={quickKindOf(draft) === QUICK_KIND.TICKETS ? 'Ticket' : 'Hash'} size={18} />}
        >
          <Stack gap="xs" align="flex-start">
            <Text size="md" ta="left">
              {quickKindOf(draft) === QUICK_KIND.TICKETS
                ? t('events:wizard.quickTickets.basicsNote', {
                    defaultValue: 'Quick ticket sale: a name and when, then where, the ticket’s price and how many, and a picture.',
                  })
                : t('events:wizard.quick.basicsNote', {
                    defaultValue: 'Quick headcount: a name and when, then a picture. Guests reply with how many are coming.',
                  })}
            </Text>
            <Button variant="subtle" size="md" px={0} onClick={() => patchDraft(leaveQuick(draft))}>
              {t('events:wizard.quick.useFull', { defaultValue: 'Use the full setup instead' })}
            </Button>
          </Stack>
        </Alert>
      ) : null}

      <TextField
        name="title"
        label={t('events:wizard.basics.title', { defaultValue: 'Event title' })}
        placeholder={
          // ⓘ Not for "Other" — "e.g. Other celebration" is no example (the quick path picks Other when no type was chosen).
          type && type.code !== 'other'
            ? t('events:wizard.basics.titlePlaceholderTyped', {
                defaultValue: 'e.g. {{name}} celebration',
                name: type.name,
              })
            : t('events:wizard.basics.titlePlaceholder', { defaultValue: 'Give your event a name' })
        }
        value={draft.title}
        onChange={handleChange}
        required
        withAsterisk
        data-autofocus
        error={getFieldError(saveError, 'title')}
      />

      {quick ? null : (
        <TextField
          name="subtitle"
          label={t('events:wizard.basics.subtitle', { defaultValue: 'Subtitle' })}
          description={t('events:wizard.basics.subtitleHint', {
            defaultValue: 'A short line shown under the title (optional)',
          })}
          placeholder={t('events:wizard.basics.subtitlePlaceholder', { defaultValue: 'Add a subtitle' })}
          value={draft.subtitle}
          onChange={handleChange}
        />
      )}

      {askOrganizer ? (
        <SelectField
          name="organizerProfileId"
          label={t('events:details.organizerPage', { defaultValue: 'Organizer page' })}
          description={t('events:wizard.basics.organizerPageHint', {
            defaultValue: 'Which of your organizer pages lists this event',
          })}
          data={organizerOptions}
          value={draft.organizerProfileId ?? null}
          onChange={(_name, value) => patchDraft({ organizerProfileId: (value as string | null) || null })}
          placeholder={t('events:details.pickOrganizerPage', { defaultValue: 'Not on an organizer page' })}
          clearable
        />
      ) : null}

      {/* #62 P6(d2) — the Overview is the line the organizer page shows with this event, so it is asked for right here,
          and only while the event is on one. The event page's own description is written on the Event page step. */}
      {draft.organizerProfileId ? (
        <TextAreaField
          size="md"
          name="summary"
          label={t('events:details.summary', { defaultValue: 'Overview' })}
          description={t('events:details.summaryHint', { defaultValue: 'A short line shown with this event on your organizer page' })}
          placeholder={t('events:wizard.basics.summaryPh', { defaultValue: 'Join us for…' })}
          value={draft.summary ?? ''}
          onChange={handleChange}
          autosize
          minRows={2}
          maxRows={5}
          // The server's limit (eventValidators: summary ≤ 512) — said here, before Next, not as a save error after it.
          maxLength={512}
        />
      ) : null}

      {quick ? null : (
        <Stack gap="xs">
          <Text fw={600} size="md">
            {t('events:wizard.basics.scheduleLabel', { defaultValue: 'When does it happen?' })}
          </Text>
          <ScheduleModeField
            name="scheduleMode"
            allowRecurring={allowRecurring}
            value={draft.scheduleMode}
            onChange={handleChange}
            fullWidth={isMobile}
          />
        </Stack>
      )}

      <SwitchField
        name="isAllDay"
        label={t('events:wizard.basics.allDay', { defaultValue: 'All day' })}
        checked={draft.isAllDay}
        onChange={handleAllDayChange}
      />

      <Stack gap="xs">
        <SimpleGrid cols={isMobile ? 1 : 2} spacing="md">
          <DateTimeField
            name="startsAt"
            label={t('events:wizard.basics.startsAt', { defaultValue: isSpan ? 'Starts' : 'Start' })}
            placeholder={t('events:wizard.basics.pickDateTime', { defaultValue: 'Pick date & time' })}
            value={draft.startsAt}
            onChange={handleStartChange}
            dateOnly={draft.isAllDay}
            // #139 — the quick path is "a name and WHEN": it goes live on Finish, so a date is required there.
            required={quick}
            disallowPast
            timeZone={draft.timezone || null}
            leftSection={<AppIcon iconName="Calendar" size={16} />}
            clearable
            error={getFieldError(saveError, 'startsAt')}
          />
          <DateTimeField
            name="endsAt"
            label={t('events:wizard.basics.endsAt', { defaultValue: 'End' })}
            placeholder={t('events:wizard.basics.pickDateTime', { defaultValue: 'Pick date & time' })}
            value={draft.endsAt}
            onChange={handleEndChange}
            dateOnly={draft.isAllDay}
            notBefore={draft.startsAt}
            leftSection={<AppIcon iconName="Calendar" size={16} />}
            clearable
            error={getFieldError(saveError, 'endsAt')}
          />
        </SimpleGrid>

        <TimezoneSelect
          name="timezone"
          label={t('events:wizard.basics.timezone', { defaultValue: 'Timezone' })}
          // The dates and times above are in THIS zone — said when this device keeps another one.
          description={eventZoneNote(t, draft.timezone) ?? undefined}
          value={draft.timezone}
          onChange={handleChange}
          error={getFieldError(saveError, 'timezone')}
        />

        <EventDurationBadge
          startsAt={draft.startsAt}
          endsAt={draft.endsAt}
          isAllDay={draft.isAllDay}
          style={{ alignSelf: 'flex-start' }}
        />
      </Stack>

      {isSpan && (
        <Alert variant="light" color="brand" icon={<AppIcon iconName="InfoCircle" size={18} />}>
          {draft.scheduleMode === SCHEDULE_MODE.AGENDA
            ? t('events:wizard.basics.agendaNote', {
                defaultValue:
                  'Set the overall dates now. To add its parts (ceremony, reception, talks…), turn on Sessions on the Features step.',
              })
            : t('events:wizard.basics.recurringNote', {
                defaultValue: 'Set the first date now — you’ll choose how it repeats in the Schedule step.',
              })}
        </Alert>
      )}
    </Stack>
  );
};
